import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import {
  Button,
  CheckBox,
  Flex,
  Form,
  Input,
  PhoneNumberInput,
  Tooltip,
  useStylesFor as stylesFor,
} from '@sine/sine-ui-components';

import { useJoiForm } from '../../../shared/customHooks';
import { inviteeSchema } from '../invitations.schemas';

const DeleteButton = styled(Button)`
  margin-top: 20px;

  ${stylesFor('tablet').andSmaller`
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    // width: 100%;
    text-align: center;
    justify-self: center;
    align-self: center;

    &::after {
      content: 'Delete';
      display: inline;
      text-transform: capitalize;
      padding: 0 0 0 4px;
    }
  `}
`;

const StyledCheckBox = styled(CheckBox)`
  margin-top: 0;
`;

export default function InviteesForm({
  invitation,
  allowMultiple,
  formsRef,
  selectedSite,
  isLoading,
  setNumberOfGuestsWithoutEmail,
  canSendEmail,
  setCanSendEmail,
}) {
  const [inviteeIds, setInviteeIds] = useState([uuidv4()]);
  const [isDeletable, setIsDeletable] = useState(false);
  const [emailInvitations, setEmailInvitations] = useState([]);

  const hasTriggeredCheckboxRef = React.useRef(false);

  useEffect(() => {
    setIsDeletable(inviteeIds.length > 1);
  }, [inviteeIds]);

  const onAddInvitee = () => {
    setInviteeIds((prev) => [...prev, uuidv4()]);
  };

  const onDeleteInvitee = (id) => {
    delete formsRef.current[id];
    setInviteeIds((prev) => prev.filter((_id) => _id !== id));
    setEmailInvitations((prev) => prev.filter((_id) => _id !== id));
  };

  const onClickSendEmail = () => {
    if (emailInvitations.length > 0) {
      setCanSendEmail(!canSendEmail);
    }
  };

  const numberOfGuestsWithoutEmail = () => {
    return inviteeIds.length - emailInvitations.length;
  };

  useEffect(() => {
    setNumberOfGuestsWithoutEmail(numberOfGuestsWithoutEmail);
  }, [emailInvitations, inviteeIds]);

  useEffect(() => {
    if (emailInvitations.length === 0 && canSendEmail) {
      setCanSendEmail(false);
      hasTriggeredCheckboxRef.current = false;
    } else if (!hasTriggeredCheckboxRef.current && emailInvitations.length > 0) {
      hasTriggeredCheckboxRef.current = true;
      setCanSendEmail(true);
    }
  }, [emailInvitations]);

  const addEmailInvitations = (id) => {
    if (!emailInvitations.includes(id)) {
      setEmailInvitations((prev) => [...prev, id]);
    }
  };

  const deleteEmailInvitations = (id) => {
    setEmailInvitations((prev) => prev.filter((_id) => _id !== id));
  };

  return (
    <>
      {inviteeIds.map((id) => (
        <InviteeForm
          key={id}
          inviteeId={id}
          formsRef={formsRef}
          invitation={invitation ? invitation : undefined}
          onDelete={isDeletable ? () => onDeleteInvitee(id) : undefined}
          selectedSite={selectedSite}
          isLoading={isLoading}
          addEmailInvitations={addEmailInvitations}
          deleteEmailInvitations={deleteEmailInvitations}
        />
      ))}

      <Flex alignItems='center'>
        <Flex grow={1}>
          <StyledCheckBox
            label={
              invitation ? (
                <>
                  Re-send invitation to guest when saving{' '}
                  <b>
                    {' '}
                    ({emailInvitations.length} of {inviteeIds.length} guest)
                  </b>
                </>
              ) : (
                <>
                  Notify guest/s with an email invitation{' '}
                  <b>
                    {' '}
                    ({emailInvitations.length} of {inviteeIds.length} guest/s)
                  </b>
                </>
              )
            }
            data-st-ext='checkbox-send-emails'
            value={canSendEmail}
            onClick={onClickSendEmail}
            disabled={!emailInvitations.length > 0}
            noSpaceReservation
          />
        </Flex>
        <Flex grow={0}>
          {allowMultiple && (
            <Button variant='tertiary' icon='add' onClick={onAddInvitee} data-st-ext='button-add-another-invitee'>
              Add another invitee
            </Button>
          )}
        </Flex>
      </Flex>
    </>
  );
}

InviteesForm.propTypes = {
  allowMultiple: PropTypes.bool.isRequired,
  formsRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  invitation: PropTypes.object,
  selectedSite: PropTypes.object,
  isLoading: PropTypes.bool,
  setNumberOfGuestsWithoutEmail: PropTypes.func,
  canSendEmail: PropTypes.bool,
  setCanSendEmail: PropTypes.func,
};

function InviteeForm({
  invitation,
  inviteeId,
  formsRef,
  onDelete,
  selectedSite,
  isLoading,
  addEmailInvitations,
  deleteEmailInvitations,
}) {
  const form = useJoiForm(inviteeSchema, {
    firstName: invitation?.firstName || '',
    lastName: invitation?.lastName || '',
    email: invitation?.email || '',
    mobile: invitation?.mobile || '',
  });

  useEffect(() => {
    if (form.values.email.length > 0) {
      addEmailInvitations(inviteeId);
    } else if (form.values.email.length === 0) {
      deleteEmailInvitations(inviteeId);
    }
  }, [form.values.email]);

  formsRef.current[inviteeId] = form;

  return (
    <Form values={form.values} errors={form.errors} onFieldChange={form.onChange}>
      <Flex gap={2} alignItems='center' data-st-ext='invitee-row'>
        <Flex grow={1} direction='column' alignItems='stretch'>
          <Input name='firstName' label='First Name' data-st-ext='first-name' autoFocus />
        </Flex>
        <Flex grow={1} direction='column' alignItems='stretch'>
          <Input name='lastName' label='Last Name' data-st-ext='last-name' />
        </Flex>
        <Flex grow={1} direction='column' alignItems='stretch'>
          <Input name='email' label='Email' data-st-ext='email' />
        </Flex>

        {!isLoading && (
          <Flex grow={1} direction='column' alignItems='stretch'>
            <PhoneNumberInput
              name='mobile'
              label='Mobile'
              data-st-ext='mobile'
              optional
              defaultCountryCode={selectedSite?.countryCode}
            />
          </Flex>
        )}

        {onDelete && (
          <Flex grow={0} direction='column' alignItems='stretch'>
            <Tooltip content='Delete'>
              <DeleteButton
                variant='tertiary'
                iconOnly
                icon='delete'
                onClick={onDelete}
                data-st-ext='delete-invitee'
                alignment='center'
              />
            </Tooltip>
          </Flex>
        )}
      </Flex>
    </Form>
  );
}

InviteeForm.propTypes = {
  inviteeId: PropTypes.string.isRequired,
  formsRef: InviteesForm.propTypes.formsRef,
  onDelete: PropTypes.func,
  invitation: PropTypes.object,
  selectedSite: PropTypes.object,
  isLoading: PropTypes.bool,
  addEmailInvitations: PropTypes.func,
  deleteEmailInvitations: PropTypes.func,
};
